import { BookOpenIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { useMemo } from 'react';
import EmptyStateNew from '../../../components/empty-state/EmptyStateNew';
import { useWorksheetHistory } from '../../../queries/use-worksheet-history';
import { Worksheet, WorksheetHistoryResult } from '../../../types/worksheet';
import WorksheetHistoryDate from './WorksheetHistoryDate';
import { Spinner } from '@cimplico/components';
type WorksheetHistoryProps = {
    worksheet: Worksheet;
};

type GroupedDates = Record<string, WorksheetHistoryResult[]>;

/**
 * Groups events by date.
 * @param history - the list of events to group
 * @returns a map of dates to event lists
 */
function groupByDate(history: WorksheetHistoryResult[]): GroupedDates {
    const groupedDates: GroupedDates = {};

    // Group events that have happened in the same day
    history.forEach((event) => {
        const date = format(new Date(event.createdAt), 'yyyy-MM-dd');

        if (groupedDates[date]) {
            groupedDates[date].push(event);
        } else {
            groupedDates[date] = [event];
        }
    });
    return groupedDates;
}

function WorksheetHistory({ worksheet }: WorksheetHistoryProps): JSX.Element {
    const historyQuery = useWorksheetHistory(worksheet.id);

    const dateGroups = useMemo(() => groupByDate(historyQuery.data?.data ?? []), [historyQuery.data?.data]);

    if (historyQuery.isLoading) {
        return (
            <div className="h-full flex items-center justify-center">
                <Spinner />
            </div>
        );
    }

    if (!historyQuery.isSuccess) {
        return <p>Error!</p>;
    }

    const { data: history } = historyQuery.data;

    if (history.length === 0) {
        return (
            <div className="mt-8">
                <EmptyStateNew
                    icon={<BookOpenIcon />}
                    helpLink="https://learn.cimplico.com/docs/worksheet-history"
                    helpText="See a log of the events that have taken place on a worksheet."
                    title="No history events"
                />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            {[...Object.keys(dateGroups)]
                .sort((a, b) => (a > b ? -1 : 1))
                .map((date, idx) => {
                    const events = dateGroups[date];
                    return <WorksheetHistoryDate key={date} events={events} date={date} defaultCollapsed={idx !== 0} />;
                })}
        </div>
    );
}
export default WorksheetHistory;
